/**
 * @fileoverview Hook for determining the currently active page based on
 * navigation context and current pathname, including breadcrumb generation.
 */

import * as React from 'react';
import { NavigationContext, RouterContext } from '../contexts';
import { getItemPath, getItemTitle, matchPath } from '../utils/navigationHelpers';
import type { ActivePage, Breadcrumb } from '../types';

export function useActivePage(): ActivePage | null {
  const navigationContext = React.useContext(NavigationContext);
  const router = React.useContext(RouterContext);
  const pathname = router?.pathname ?? '/';
  const activeItem = matchPath(navigationContext, pathname);

  return React.useMemo(() => {
    if (!activeItem) {
      return null;
    }

    const breadcrumbs: Breadcrumb[] = [];

    // Always use "Home" as the root breadcrumb (except when on Home page itself)
    const isHomePage = pathname === '/' || pathname === '';
    if (!isHomePage) {
      breadcrumbs.push({
        title: 'Home',
        path: '/',
      });
    }

    const segments = pathname.split('/').filter(Boolean);
    
    // Handle inbox path '/inbox' - add the active item as breadcrumb
    if (segments.length === 1 && segments[0] === 'inbox' && pathname === '/inbox') {
      breadcrumbs.push({
        title: getItemTitle(activeItem),
        path: getItemPath(navigationContext, activeItem),
      });
    }

    let prefix = '';
    for (const segment of segments) {
      const path = `${prefix}/${segment}`;
      prefix = path;
      // Skip adding root path to breadcrumbs since "Home" is already added
      if (path === '/' || path === '') {
        continue;
      }
      const item = matchPath(navigationContext, path);
      if (!item) {
        continue;
      }
      const itemPath = getItemPath(navigationContext, item);
      const lastCrumb = breadcrumbs[breadcrumbs.length - 1];
      if (lastCrumb?.path !== itemPath) {
        breadcrumbs.push({
          title: getItemTitle(item),
          path: itemPath,
        });
      }
    }

    return {
      title: getItemTitle(activeItem),
      path: getItemPath(navigationContext, activeItem),
      breadcrumbs,
    };
  }, [activeItem, pathname, navigationContext]);
}
